import { LocalTypes } from '../types/TitleAnalysis';
import { merge } from 'lodash';
import Loader, { loadWrap } from './mixins/Loader';
import tokenService from '@/services/TokenService';
import axios from 'axios';

export default function ({ baseURL, endpoint }, titleTuneConfig) {
    const apiService = axios.create({
        baseURL,
        timeout: 30000,
    });
    const titleTuneService = axios.create({
        baseURL: titleTuneConfig.baseURL,
        timeout: 30000,
    });

    return merge(Loader(), {
        state: {
            score: 0,
            /**
             * List of title suggestions in the form `[{ title: string, score: number }]`
             * @type Array<Object>
             */
            suggestions: [],
            suggestionsLoaded: false,
            /**Indicates if an error occured while loading title suggestions**/
            suggestionsError: undefined,
        },
        getters: {},
        actions: {
            async [LocalTypes.actions.LOAD_SCORE]({ commit }, { title }) {
                commit(LocalTypes.mutations.RESET_SUGGESTIONS_ERROR);
                await loadWrap(commit, async () => {
                    const headers =
                        tokenService.getAuthenticationHeader()['headers'];
                    const params = { title };
                    const response = await apiService.get(endpoint, {
                        params,
                        headers,
                    });
                    commit(
                        LocalTypes.mutations.SET_SCORE,
                        response.data.rating,
                    );
                });
            },
            /**
             * Loads alternative suggestions for a job ad title.
             *
             * In contrast to the other analysis, this is not triggered automatically,
             * because the api calls cost money.
             * @param commit
             * @param {Number} jobAdId
             * @returns {Promise<void>}
             */
            async [LocalTypes.actions.LOAD_SUGGESTIONS]({ commit }, jobAdId) {
                commit(LocalTypes.mutations.RESET_SUGGESTIONS_ERROR);
                try {
                    const headers =
                        tokenService.getAuthenticationHeader().headers;
                    const response = await titleTuneService.get(
                        `${titleTuneConfig.endpoints.generate}/${jobAdId}`,
                        { headers },
                    );
                    const items = response.data['generated_titles_data'];
                    const suggestions = items.map((suggestion) => ({
                        title: suggestion.title,
                        score: suggestion.rating * 100,
                    }));
                    commit(LocalTypes.mutations.SET_SUGGESTIONS, suggestions);
                } catch (error) {
                    commit(LocalTypes.mutations.SET_SUGGESTIONS_ERROR, error);
                }
            },
            async [LocalTypes.actions.LOAD_CUSTOM_SUGGESTIONS](
                { commit },
                { title, text },
            ) {
                commit(LocalTypes.mutations.RESET_SUGGESTIONS_ERROR);
                try {
                    const headers =
                        tokenService.getAuthenticationHeader().headers;
                    const response = await titleTuneService.post(
                        titleTuneConfig.endpoints.generate,
                        {
                            jobad_title: title,
                            jobad_text: text,
                        },
                        { headers },
                    );
                    const items = response.data['generated_titles_data'];
                    const suggestions = items.map((suggestion) => ({
                        title: suggestion.title,
                        score: suggestion.rating * 100,
                    }));

                    commit(LocalTypes.mutations.SET_SUGGESTIONS, suggestions);
                } catch (error) {
                    commit(LocalTypes.mutations.SET_SUGGESTIONS_ERROR, error);
                }
            },
            async [LocalTypes.actions.SEND_FEEDBACK](
                _,
                { title, original_title, feedback },
            ) {
                const headers = tokenService.getAuthenticationHeader().headers;
                try {
                    await axios({
                        method: 'put',
                        baseURL: titleTuneConfig.baseURL,
                        url: titleTuneConfig.endpoints.feedback,
                        headers,
                        data: {
                            jobad_title: title,
                            original_title: original_title,
                            feedback: feedback,
                        },
                    });
                } catch (error) {
                    console.error('Error sending feedback:', error);
                }
            },
        },
        mutations: {
            [LocalTypes.mutations.SET_SCORE]: (state, score) => {
                if (score < 0.01) {
                    score = 0.01;
                }
                state.score = score;
            },
            [LocalTypes.mutations.SET_SUGGESTIONS]: (state, suggestions) => {
                state.suggestions = suggestions;
                state.suggestionsLoaded = true;
            },
            [LocalTypes.mutations.RESET_SUGGESTIONS]: (state) => {
                state.suggestions = [];
                state.suggestionsLoaded = false;
            },
            [LocalTypes.mutations.SET_SUGGESTIONS_ERROR]: (state, error) => {
                state.suggestionsError = error;
            },
            [LocalTypes.mutations.RESET_SUGGESTIONS_ERROR]: (state) => {
                state.suggestionsError = undefined;
            },
        },
    });
}
